import { useTranslation } from 'react-i18next'
import { RuuviTag } from '../types'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  IconButtonProps,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuItemProps,
  MenuProps,
  Tooltip
} from '@mui/material'
import { History, LinkOff, MoreVert, Notifications, ShowChart } from '@mui/icons-material'
import useMenu from '../hooks/useMenu'
import { usePolicy } from '../hooks'
import useDialog from '../hooks/useDialog'
import AssetHistoryDialog from './AssetHistoryDialog'
import { NavLink } from 'react-router-dom'
import RuuviTagDetachButton from './RuuviTagDetachButton'
import { useState } from 'react'

interface RuuviTagMenuItemAssetHistoryProps extends Omit<MenuItemProps, 'children'> {
  ruuviTag: RuuviTag
}

function RuuviTagMenuItemAssetHistory({ ruuviTag }: RuuviTagMenuItemAssetHistoryProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The dialog state.
   */
  const { open, handleOpen, handleClose } = useDialog()

  return (
    <>
      <MenuItem onClick={handleOpen}>
        <ListItemIcon>
          <History />
        </ListItemIcon>
        <ListItemText>{t('assetHistory')}</ListItemText>
      </MenuItem>

      <AssetHistoryDialog assetHistory={ruuviTag.assetHistory} open={open} onClose={handleClose} />
    </>
  )
}

interface RuuviTagDetachMenuItemProps extends Omit<MenuItemProps, 'onClick' | 'children'> {
  ruuviTag: RuuviTag
  afterUpdate?: (ruuviTag: RuuviTag) => void
}

function RuuviTagDetachMenuItem({ ruuviTag, afterUpdate, ...props }: RuuviTagDetachMenuItemProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The policy.
   */
  const { canEditRuuviTag } = usePolicy()

  /**
   * The dialog.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The loading state.
   */
  const [isLoading, setLoading] = useState<boolean>(false)

  /**
   * Handle the before submit event.
   */
  function handleBeforeSubmit() {
    setLoading(true)
  }

  /**
   * Handle the "after" update event.
   */
  function handleAfterSubmit(ruuviTag: RuuviTag) {
    setLoading(false)
    afterUpdate?.(ruuviTag)
    closeDialog()
  }

  return (
    <>
      {canEditRuuviTag(ruuviTag) && ruuviTag?.asset?.projectId !== null && (
        <MenuItem onClick={openDialog} {...props}>
          <ListItemIcon>
            <LinkOff />
          </ListItemIcon>
          <ListItemText>{t('ruuvitag:detachFromProject')}</ListItemText>
        </MenuItem>
      )}

      <Dialog open={open} maxWidth="sm" fullWidth onClose={closeDialog}>
        <DialogTitle>{t('ruuvitag:detachRuuviTagFromProject')}</DialogTitle>

        <DialogContent>
          <Alert severity="warning">
            {t('ruuvitag:detachRuuviTagFromProjectWarning', {
              project: ruuviTag.asset?.project?.name
            })}
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button
            variant="text"
            color="inherit"
            disabled={isLoading}
            onClick={closeDialog}
            autoFocus
          >
            {t('actions:cancel')}
          </Button>

          <RuuviTagDetachButton
            variant="contained"
            color="warning"
            buttonText={t('actions:confirm')}
            ruuviTag={ruuviTag}
            beforeSubmit={handleBeforeSubmit}
            afterSubmit={handleAfterSubmit}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}

export interface RuuviTagMenuProps
  extends Omit<MenuProps, 'anchorEl' | 'open' | 'onClose' | 'onOpen' | 'children'> {
  ruuviTag: RuuviTag
  onUpdate?: (ruuviTag: RuuviTag) => void
  IconButtonProps?: Omit<IconButtonProps, 'onClick' | 'children'>
}

export default function RuuviTagMenu({
  ruuviTag,
  onUpdate,
  IconButtonProps,
  ...props
}: RuuviTagMenuProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The policies.
   */
  const { isSuperadmin, canEditRuuviTag } = usePolicy()

  /**
   * The menu hook.
   */
  const { anchorEl, isMenuOpen, openMenu, closeMenu } = useMenu()

  return (
    <>
      <Tooltip title={t('actions')}>
        <IconButton color="inherit" {...IconButtonProps} onClick={openMenu}>
          <MoreVert fontSize="inherit" />
        </IconButton>
      </Tooltip>

      <Menu
        open={isMenuOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        {...props}
        onClose={closeMenu}
      >
        <MenuItem component={NavLink} to={`/ruuvi-tags/${ruuviTag.id}/stats`}>
          <ListItemIcon>
            <ShowChart />
          </ListItemIcon>
          <ListItemText primary={t('stats')} />
        </MenuItem>
        {isSuperadmin && <RuuviTagMenuItemAssetHistory ruuviTag={ruuviTag} />}
        {isSuperadmin && (
          <MenuItem component={NavLink} to={`/ruuvi-tags/${ruuviTag.id}/alerts`}>
            <ListItemIcon>
              <Notifications />
            </ListItemIcon>
            <ListItemText primary={t('alertManagement')} />
          </MenuItem>
        )}

        {canEditRuuviTag(ruuviTag) && ruuviTag?.asset?.project && <Divider />}

        {canEditRuuviTag(ruuviTag) && ruuviTag?.asset?.project && (
          <RuuviTagDetachMenuItem ruuviTag={ruuviTag} afterUpdate={onUpdate} />
        )}
      </Menu>
    </>
  )
}
